import React, { useEffect, useState } from "react";
import api from "../api";
import { useToast } from "../hooks/use-toast";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";

function Card({ children, className = "" }) {
  return <div className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm ${className}`}>{children}</div>;
}

const STATUS_FLOW = ["Draft", "Pending Review", "Approved", "Published", "Paused", "Closed"];

export default function AdminPrograms() {
  const { toast } = useToast();
  const [rows, setRows] = useState([]);
  const [contractors, setContractors] = useState([]);
  const [payFor, setPayFor] = useState(null);
  const [assignFor, setAssignFor] = useState(null);
  const [payForm, setPayForm] = useState({ contractor_pay_rate: "", contractor_training_pay_rate: "", pay_review_status: "Approved", notes: "" });
  const [assignId, setAssignId] = useState("");

  const load = () => api.get("/admin/programs").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => {
    load();
    api.get("/admin/users").then((r) => setContractors(r.data.filter((u) => (u.role || "applicant") === "applicant"))).catch(() => {});
  }, []);

  const setStatus = async (p, status) => {
    try { await api.post(`/admin/programs/${p.id}/status`, { status }); toast({ title: `Program ${status}` }); load(); }
    catch (e) { toast({ title: "Failed", description: e.response?.data?.detail || "", variant: "destructive" }); }
  };

  const openPay = (p) => {
    setPayForm({
      contractor_pay_rate: p.contractor_pay_rate ?? "",
      contractor_training_pay_rate: p.contractor_training_pay_rate ?? "",
      pay_review_status: p.pay_review_status || "Approved", notes: "",
    });
    setPayFor(p);
  };
  const savePay = async () => {
    try {
      await api.put(`/admin/programs/${payFor.id}/pay`, {
        contractor_pay_rate: payForm.contractor_pay_rate === "" ? null : Number(payForm.contractor_pay_rate),
        contractor_training_pay_rate: payForm.contractor_training_pay_rate === "" ? null : Number(payForm.contractor_training_pay_rate),
        pay_review_status: payForm.pay_review_status, notes: payForm.notes,
      });
      toast({ title: "Contractor pay updated", description: "Saved with audit trail." });
      setPayFor(null); load();
    } catch (e) { toast({ title: "Failed", description: e.response?.data?.detail || "", variant: "destructive" }); }
  };

  const saveAssign = async () => {
    try { await api.post("/admin/programs/assign", { program_id: assignFor.id, contractor_id: assignId }); toast({ title: "Contractor assigned" }); setAssignFor(null); setAssignId(""); load(); }
    catch (e) { toast({ title: "Failed", description: e.response?.data?.detail || "", variant: "destructive" }); }
  };
  const unassign = async (p, a) => {
    if (!window.confirm(`Remove ${a.name} from ${p.name}?`)) return;
    try { await api.delete("/admin/programs/assign", { params: { program_id: p.id, contractor_id: a.contractor_id } }); toast({ title: "Contractor removed from program" }); load(); }
    catch (e) { toast({ title: "Failed", description: e.response?.data?.detail || "", variant: "destructive" }); }
  };

  const money = (v) => (v == null || v === "" ? "—" : `$${Number(v).toFixed(2)}`);

  return (
    <div className="space-y-4" data-testid="admin-programs">
      <Card>
        <h2 className="text-base font-bold text-slate-900">Client Programs</h2>
        <p className="text-sm text-slate-500">Review client-submitted programs, control the contractor-facing pay rate (clients never see it), publish, and assign contractors.</p>
      </Card>

      {rows.length === 0 && <Card><p className="text-sm text-slate-400">No programs submitted yet.</p></Card>}

      {rows.map((p) => (
        <Card key={p.id} data-testid={`program-${p.id}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="font-bold text-slate-900">{p.name} <span className="text-sm font-normal text-slate-400">· {p.company_name}</span></div>
              <div className="text-sm text-slate-500">{p.job_title} · {p.work_type} · {p.openings} openings</div>
              <div className="mt-1 inline-flex rounded-full bg-violet-50 px-2 py-0.5 text-xs font-semibold text-violet-700" data-testid={`program-status-${p.id}`}>{p.status}</div>
            </div>
            <div className="flex flex-wrap gap-2">
              <select className="rounded-lg border border-slate-200 px-2 py-1.5 text-sm" value={p.status} onChange={(e) => setStatus(p, e.target.value)} data-testid={`program-status-select-${p.id}`}>
                {STATUS_FLOW.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
              <Button variant="outline" size="sm" onClick={() => openPay(p)} data-testid={`edit-pay-${p.id}`}>Pay</Button>
              <Button variant="outline" size="sm" onClick={() => setAssignFor(p)} data-testid={`assign-${p.id}`}>Assign</Button>
            </div>
          </div>
          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <div className="rounded-lg border border-slate-100 p-3"><div className="text-[11px] uppercase text-slate-400">Client offer</div><div className="font-semibold text-slate-800">{money(p.client_offered_pay_rate)}</div></div>
            <div className="rounded-lg border border-slate-100 p-3"><div className="text-[11px] uppercase text-slate-400">Contractor rate</div><div className="font-semibold text-emerald-700" data-testid={`contractor-rate-${p.id}`}>{money(p.contractor_pay_rate)}</div></div>
            <div className="rounded-lg border border-slate-100 p-3"><div className="text-[11px] uppercase text-slate-400">Client training</div><div className="font-semibold text-slate-800">{money(p.client_offered_training_rate)}</div></div>
            <div className="rounded-lg border border-slate-100 p-3"><div className="text-[11px] uppercase text-slate-400">Pay review</div><div className="font-semibold text-slate-800">{p.pay_review_status}</div></div>
          </div>
          {(p.assigned || []).length > 0 && (
            <div className="mt-3 flex flex-wrap gap-2" data-testid={`program-assigned-${p.id}`}>
              {p.assigned.map((a) => (
                <span key={a.contractor_id} className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-slate-50 px-2.5 py-1 text-xs text-slate-700">
                  {a.name}
                  <button onClick={() => unassign(p, a)} className="font-bold text-rose-500 hover:text-rose-700" aria-label="Remove" data-testid={`unassign-${p.id}-${a.contractor_id}`}>×</button>
                </span>
              ))}
            </div>
          )}
        </Card>
      ))}

      <Dialog open={!!payFor} onOpenChange={(o) => !o && setPayFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Contractor-facing pay — {payFor?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg bg-slate-50 p-3 text-sm text-slate-600">Client offered: <strong>{money(payFor?.client_offered_pay_rate)}</strong> (the client cannot see or set the rate below)</div>
            <div className="space-y-1.5"><Label>Contractor pay rate ($)</Label><Input type="number" value={payForm.contractor_pay_rate} onChange={(e) => setPayForm({ ...payForm, contractor_pay_rate: e.target.value })} data-testid="pay-contractor-rate" /></div>
            <div className="space-y-1.5"><Label>Contractor training rate ($)</Label><Input type="number" value={payForm.contractor_training_pay_rate} onChange={(e) => setPayForm({ ...payForm, contractor_training_pay_rate: e.target.value })} data-testid="pay-contractor-training" /></div>
            <div className="space-y-1.5"><Label>Review status</Label>
              <select className="w-full rounded-lg border border-slate-200 px-2 py-2 text-sm" value={payForm.pay_review_status} onChange={(e) => setPayForm({ ...payForm, pay_review_status: e.target.value })} data-testid="pay-review-status">
                {["Not Reviewed", "Under Review", "Approved", "Rejected", "Modified"].map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div className="space-y-1.5"><Label>Notes</Label><Input value={payForm.notes} onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })} placeholder="Reason for this rate" data-testid="pay-notes" /></div>
          </div>
          <DialogFooter><Button onClick={savePay} className="btn-primary-gradient border-0 text-white" data-testid="save-pay-btn">Save pay</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!assignFor} onOpenChange={(o) => !o && setAssignFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Assign contractor — {assignFor?.name}</DialogTitle></DialogHeader>
          <div className="space-y-1.5">
            <Label>Contractor</Label>
            <select className="w-full rounded-lg border border-slate-200 px-2 py-2 text-sm" value={assignId} onChange={(e) => setAssignId(e.target.value)} data-testid="assign-contractor-select">
              <option value="">Select a contractor…</option>
              {contractors.map((c) => <option key={c.id} value={c.id}>{c.first_name} {c.last_name} ({c.email})</option>)}
            </select>
          </div>
          <DialogFooter><Button disabled={!assignId} onClick={saveAssign} className="btn-primary-gradient border-0 text-white" data-testid="save-assign-btn">Assign</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
